// API Middleware for handling authentication and other cross-cutting concerns
import { SERVER } from './ApiUrls'

export interface ApiError extends Error {
  status?: number
  data?: any
}

export interface MiddlewareOptions {
  skipAuth?: boolean
  showAuthErrors?: boolean
  retryOnAuth?: boolean
}

// Global event system for auth events
type AuthEventType = 'tokenExpired' | 'unauthorized' | 'tokenRefreshed' | 'authError'

type EventHandler = (...args: any[]) => void

interface AuthEventHandlers {
  tokenExpired: EventHandler[]
  unauthorized: EventHandler[]
  tokenRefreshed: EventHandler[]
  authError: EventHandler[]
}

class ApiMiddleware {
  private eventHandlers: AuthEventHandlers = {
    tokenExpired: [],
    unauthorized: [],
    tokenRefreshed: [],
    authError: []
  }

  private isRefreshing = false
  private failedQueue: Array<{
    resolve: (value: any) => void
    reject: (error: any) => void
    config: RequestInit & { url: string }
  }> = []

  // Event subscription system
  on(event: AuthEventType, handler: EventHandler) {
    this.eventHandlers[event].push(handler)
  }

  off(event: AuthEventType, handler: EventHandler) {
    const handlers = this.eventHandlers[event]
    const index = handlers.indexOf(handler)
    if (index > -1) {
      handlers.splice(index, 1)
    }
  }

  private emit(event: AuthEventType, ...args: any[]) {
    this.eventHandlers[event].forEach(handler => {
      try {
        handler(...args)
      } catch (error) {
        console.error(`Error in ${event} handler:`, error)
      }
    })
  }

  // Clear all auth data and redirect to login
  private clearAuthAndRedirect() {
    localStorage.removeItem('AccessToken')
    localStorage.removeItem('RefreshToken')
    localStorage.removeItem('UserId')
    // Set a flag to indicate session expiration
    localStorage.setItem('sessionExpired', 'true')
    this.emit('unauthorized')
    // Add a small delay to allow any pending operations to complete
    setTimeout(() => {
      window.location.href = '/login'
    }, 100)
  }

  // Refresh access token
  private async refreshAccessToken(): Promise<string | null> {
    const refreshToken = localStorage.getItem('RefreshToken')
    
    if (!refreshToken) {
      return null
    }

    try {
      const response = await fetch(`${SERVER}auth/refresh-token/`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ refresh: refreshToken })
      })

      if (!response.ok) {
        throw new Error('Refresh token failed')
      }

      const data = await response.json()
      
      if (data.access) {
        const newAccessToken = `Bearer ${data.access}`
        localStorage.setItem('AccessToken', newAccessToken)
        this.emit('tokenRefreshed', newAccessToken)
        return newAccessToken
      } else {
        throw new Error('No access token in refresh response')
      }
    } catch (error) {
      console.error('Token refresh failed:', error)
      this.emit('tokenExpired')
      return null
    }
  }

  // Process failed requests after token refresh
  private processQueue(error: any = null, token: string | null = null) {
    this.failedQueue.forEach(({ resolve, reject, config }) => {
      if (error) {
        reject(error)
      } else if (token) {
        // Update authorization header and retry
        config.headers = {
          ...config.headers,
          'Authorization': token
        }
        resolve(this.makeRequest(config.url, config))
      }
    })
    
    this.failedQueue = []
  }

  // Core request method with middleware logic
  async makeRequest<T = any>(
    url: string, 
    options: RequestInit = {},
    middlewareOptions: MiddlewareOptions = {}
  ): Promise<T> {
    const {
      skipAuth = false,
      showAuthErrors = true,
      retryOnAuth = true
    } = middlewareOptions

    // Ensure we have the full URL
    const fullUrl = url.startsWith('http') ? url : `${SERVER}${url.startsWith('/') ? url.slice(1) : url}`
    
    // Default headers
    const defaultHeaders: Record<string, string> = {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    }

    // Add authorization header if not skipped
    if (!skipAuth) {
      const accessToken = localStorage.getItem('AccessToken')
      if (accessToken) {
        defaultHeaders['Authorization'] = accessToken
      }
    }

    // Merge headers
    const config: RequestInit = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
    }

    // Remove Content-Type for certain methods
    if (options.method === 'DELETE' || options.method === 'GET') {
      delete (config.headers as any)['Content-Type']
    }

    try {
      const response = await fetch(fullUrl, config)
      
      // Handle different response statuses
      if (response.ok) {
        // Success - return the data
        const contentType = response.headers.get('content-type')
        if (contentType && contentType.includes('application/json')) {
          return await response.json()
        } else {
          return response.text() as any
        }
      }

      // Handle error responses
      return await this.handleErrorResponse(response, { url: fullUrl, ...config }, retryOnAuth, showAuthErrors)
      
    } catch (error: any) {
      // Network or other errors
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        const networkError: ApiError = new Error('Network error - please check your connection')
        networkError.status = 0
        throw networkError
      }
      throw error
    }
  }

  private async handleErrorResponse(
    response: Response, 
    originalConfig: RequestInit & { url: string },
    retryOnAuth: boolean,
    showAuthErrors: boolean
  ): Promise<any> {
    const status = response.status
    let errorData: any = {}
    
    try {
      errorData = await response.json()
    } catch {
      // If JSON parsing fails, create a basic error object
      errorData = { message: response.statusText }
    }

    // Handle 401 Unauthorized
    if (status === 401) {
      if (retryOnAuth && !originalConfig.url.includes('refresh-token')) {
        return this.handle401Error(originalConfig, errorData, showAuthErrors)
      } else {
        // Don't retry or this is already a retry
        this.clearAuthAndRedirect()
        // Don't throw the error - let the redirect handle it
        // This prevents components from catching 401 errors and showing them as toasts
        return Promise.reject(new Error('Authentication required - redirecting to login'))
      }
    }

    // Handle 403 Forbidden
    if (status === 403) {
      const forbiddenError: ApiError = new Error('Access forbidden - insufficient permissions')
      forbiddenError.status = 403
      forbiddenError.data = errorData
      this.emit('authError', forbiddenError)
      throw forbiddenError
    }

    // Handle other error statuses
    const apiError: ApiError = new Error(errorData.message || `HTTP error! status: ${status}`)
    apiError.status = status
    apiError.data = errorData
    throw apiError
  }

  private async handle401Error(
    originalConfig: RequestInit & { url: string },
    errorData: any,
    showAuthErrors: boolean
  ) {
    if (this.isRefreshing) {
      // If already refreshing, queue this request
      return new Promise((resolve, reject) => {
        this.failedQueue.push({ resolve, reject, config: originalConfig })
      })
    }

    this.isRefreshing = true

    try {
      const newToken = await this.refreshAccessToken()
      
      if (newToken) {
        this.processQueue(null, newToken)
        
        // Retry the original request with new token
        const retryConfig = {
          ...originalConfig,
          headers: {
            ...originalConfig.headers,
            'Authorization': newToken
          }
        }
        
        const response = await fetch(originalConfig.url, retryConfig)
        
        if (response.ok) {
          const contentType = response.headers.get('content-type')
          if (contentType && contentType.includes('application/json')) {
            return await response.json()
          } else {
            return response.text()
          }
        } else {
          // If retry also fails, handle it normally
          await this.handleErrorResponse(response, originalConfig, false, showAuthErrors)
        }
        
      } else {
        // Refresh failed
        this.processQueue(new Error('Token refresh failed'), null)
        this.clearAuthAndRedirect()
        // Don't throw the error - let the redirect handle it
        // This prevents components from catching 401 errors and showing them as toasts
        return Promise.reject(new Error('Session expired - redirecting to login'))
      }
    } catch (error) {
      this.processQueue(error, null)
      throw error
    } finally {
      this.isRefreshing = false
    }
  }

  // Convenience methods for different HTTP methods
  async get<T = any>(url: string, options: MiddlewareOptions = {}): Promise<T> {
    return this.makeRequest<T>(url, { method: 'GET' }, options)
  }

  async post<T = any>(url: string, data?: any, options: MiddlewareOptions = {}): Promise<T> {
    return this.makeRequest<T>(url, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined
    }, options)
  }

  async put<T = any>(url: string, data?: any, options: MiddlewareOptions = {}): Promise<T> {
    return this.makeRequest<T>(url, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined
    }, options)
  }

  async patch<T = any>(url: string, data?: any, options: MiddlewareOptions = {}): Promise<T> {
    return this.makeRequest<T>(url, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined
    }, options)
  }

  async delete<T = any>(url: string, options: MiddlewareOptions = {}): Promise<T> {
    return this.makeRequest<T>(url, { method: 'DELETE' }, options)
  }
}

// Export singleton instance
export const apiMiddleware = new ApiMiddleware()

// Export default for easier imports
export default apiMiddleware
